import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, ViewChildren, Input, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { FormControl, Validators, FormArray, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

class OTPInput {
    constructor() {
        this.#size = 4;
        this.inputs = this.getFormArray(this.#size);
        // there is a way to do this without maintaining a variable for focus
        // but safari has an issue where the order of events (keydown, input, keyup) might get jumbled in some scenarios
        // hence we are sticking with using a variable here for referencing the position to be focussed on
        this.#scheduledFocus = null;
        this.label = '';
        /**
         * Explicitly send this flag when backend sends some erroneous response for OTP
         */
        this.isInvalid = false;
        /**
         * Error message to be shown below the otp input
         */
        this.errorMessage = '';
        this.style = {};
        this.autoFocus = false;
    }
    #size;
    // there is a way to do this without maintaining a variable for focus
    // but safari has an issue where the order of events (keydown, input, keyup) might get jumbled in some scenarios
    // hence we are sticking with using a variable here for referencing the position to be focussed on
    #scheduledFocus;
    set size(_size) {
        this.#size = _size;
        this.inputs = this.getFormArray(_size);
    }
    // this is for mainly handling autofocus
    ngAfterViewInit() {
        if (this.inputEls && this.inputEls.length > 0 && this.autoFocus)
            this.inputEls.get(0)?.nativeElement.focus();
    }
    // All handlers from here on
    handleInput() {
        this.#updateWiredValue();
        if (this.#scheduledFocus != null) {
            this.#focusInput(this.#scheduledFocus);
            this.#scheduledFocus = null;
        }
    }
    handleKeyDown(e, idx) {
        // allow tabs to switch between inputs
        if (e.key === 'Tab') {
            return;
        }
        if (e.key === 'Backspace' || e.key === 'Delete') {
            // In case the input is already filled, delete the value and focus on the same input only
            if (this.inputs.controls[idx].value) {
                this.#scheduledFocus = idx;
            }
            else {
                // In case of delete or backspace queue up the focus to be done on previous index if the current input isn't filled
                if (idx > 0) {
                    this.#scheduledFocus = idx - 1;
                    this.#focusInput(this.#scheduledFocus);
                }
            }
            return;
        }
        const isDigit = /\d/.test(e.key);
        // Safari fires Cmd + V through keyPress event as well
        // So we need to handle it here and let it through, this is then handled by the paste handler
        if (e.key === 'v' && e.metaKey) {
            return;
        }
        // return if a non-numeric and unallowed character is pressed
        if (!isDigit) {
            e.preventDefault();
            return;
        }
        if (isDigit && idx + 1 < this.#size) {
            // If user inputs digits & we are not on the last input we want to advance the focus
            this.#scheduledFocus = idx + 1;
        }
        if (isDigit && this.inputs.controls[idx].value) {
            // If user deselects an input which already has a value we want to clear it so that it doesn't have more than 1 digit
            this.inputs.controls[idx].setValue('');
        }
    }
    handleFocus(e) {
        e.target.focus();
    }
    handlePaste(event, idx) {
        event.preventDefault();
        if (idx !== 0) {
            // If the target input is not the first one - ignore
            // We only allow pasting when the first box is selected, not from any other box
            return;
        }
        const pasteData = event.clipboardData?.getData('text');
        const regex = new RegExp(`\\d{${this.#size}}`);
        if (!pasteData || !regex.test(pasteData)) {
            // If there is nothing to be pasted or the pasted data does not comply with the required format - ignore the event
            return;
        }
        for (let i = 0; i < pasteData.length; i++) {
            this.inputs.controls[i].setValue(pasteData[i]);
        }
        this.#focusInput(this.inputEls.length - 1);
        this.#updateWiredValue();
        this.onTouched?.();
    }
    // Private methods referenced in handlers from here on
    #focusInput(idx) {
        // In order not to interfere with the input we setTimeout before advancing the focus
        // Without setTimeout this would create issues
        setTimeout(() => this.inputEls.get(idx)?.nativeElement.focus());
    }
    #updateWiredValue() {
        // We want to expose the value as a plain string
        setTimeout(() => this.onChange?.(this.inputs.value.join('')));
    }
    getFormArray(_size) {
        const arr = [];
        for (let i = 0; i < _size; i++) {
            arr.push(new FormControl('', Validators.required));
        }
        return new FormArray(arr);
    }
    // All the ControlValueAccessor overrides from here on
    writeValue() {
        // this is called when the form calls reset
        this.inputs.setValue(new Array(this.#size).fill(''));
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        if (isDisabled) {
            this.inputs.disable();
        }
        else {
            this.inputs.enable();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: OTPInput, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.8", type: OTPInput, selector: "pv-otp-input", inputs: { size: "size", label: "label", isInvalid: "isInvalid", errorMessage: "errorMessage", style: "style", styleClass: "styleClass", autoFocus: "autoFocus" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: OTPInput,
                multi: true,
            },
        ], viewQueries: [{ propertyName: "inputEls", predicate: ["inputEl"], descendants: true }], ngImport: i0, template: "<div\n  class=\"pv-otp-input-container flex flex-column gap-1 w-max\"\n  [ngClass]=\"styleClass\"\n  [style]=\"this.style?.container\">\n  <span\n    *ngIf=\"this.label\"\n    class=\"pv-otp-input-label mb-3 surface-text-subtle-low-contrast text-sm\"\n    [style]=\"this.style?.label\">\n    {{ this.label }}\n  </span>\n  <div\n    class=\"pv-otp-input flex flex-row gap-2\"\n    [style]=\"this.style?.inputContainer\">\n    <input\n      #inputEl\n      [class.is-invalid-otp]=\"this.isInvalid\"\n      class=\"pv-otp-input-ele text-base w-4rem h-4rem md:w-3rem md:h-3rem text-center font-bold\"\n      *ngFor=\"let input of this.inputs.controls; let i = index\"\n      [attr.autocomplete]=\"'one-time-code'\"\n      (focus)=\"this.handleFocus($event)\"\n      (blur)=\"this.onTouched?.()\"\n      (input)=\"this.handleInput()\"\n      (keydown)=\"this.handleKeyDown($event, i)\"\n      (paste)=\"this.handlePaste($event, i)\"\n      [formControl]=\"$any(input)\"\n      type=\"text\"\n      inputmode=\"numeric\"\n      [style]=\"this.style?.input\" />\n  </div>\n  <span\n    *ngIf=\"this.isInvalid && this.errorMessage\"\n    class=\"pv-invalid-otp-label feedback-text-negative w-max text-sm mt-2 max-w-full\"\n    [style]=\"this.style?.error\">\n    {{ this.errorMessage }}\n  </span>\n</div>\n", styles: [".pv-otp-input-ele{border-radius:10%;outline-color:var(--primary-color);border:.6px solid var(--gray-400)}.pv-otp-input-ele.is-invalid-otp{border-color:var(--feedback-background-negative-high-contrast);outline-color:var(--feedback-background-negative-high-contrast)}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: OTPInput, decorators: [{
            type: Component,
            args: [{ selector: 'pv-otp-input', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: OTPInput,
                            multi: true,
                        },
                    ], encapsulation: ViewEncapsulation.None, template: "<div\n  class=\"pv-otp-input-container flex flex-column gap-1 w-max\"\n  [ngClass]=\"styleClass\"\n  [style]=\"this.style?.container\">\n  <span\n    *ngIf=\"this.label\"\n    class=\"pv-otp-input-label mb-3 surface-text-subtle-low-contrast text-sm\"\n    [style]=\"this.style?.label\">\n    {{ this.label }}\n  </span>\n  <div\n    class=\"pv-otp-input flex flex-row gap-2\"\n    [style]=\"this.style?.inputContainer\">\n    <input\n      #inputEl\n      [class.is-invalid-otp]=\"this.isInvalid\"\n      class=\"pv-otp-input-ele text-base w-4rem h-4rem md:w-3rem md:h-3rem text-center font-bold\"\n      *ngFor=\"let input of this.inputs.controls; let i = index\"\n      [attr.autocomplete]=\"'one-time-code'\"\n      (focus)=\"this.handleFocus($event)\"\n      (blur)=\"this.onTouched?.()\"\n      (input)=\"this.handleInput()\"\n      (keydown)=\"this.handleKeyDown($event, i)\"\n      (paste)=\"this.handlePaste($event, i)\"\n      [formControl]=\"$any(input)\"\n      type=\"text\"\n      inputmode=\"numeric\"\n      [style]=\"this.style?.input\" />\n  </div>\n  <span\n    *ngIf=\"this.isInvalid && this.errorMessage\"\n    class=\"pv-invalid-otp-label feedback-text-negative w-max text-sm mt-2 max-w-full\"\n    [style]=\"this.style?.error\">\n    {{ this.errorMessage }}\n  </span>\n</div>\n", styles: [".pv-otp-input-ele{border-radius:10%;outline-color:var(--primary-color);border:.6px solid var(--gray-400)}.pv-otp-input-ele.is-invalid-otp{border-color:var(--feedback-background-negative-high-contrast);outline-color:var(--feedback-background-negative-high-contrast)}\n"] }]
        }], propDecorators: { inputEls: [{
                type: ViewChildren,
                args: ['inputEl']
            }], size: [{
                type: Input
            }], label: [{
                type: Input
            }], isInvalid: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], style: [{
                type: Input
            }], styleClass: [{
                type: Input
            }], autoFocus: [{
                type: Input
            }] } });

class PvOTPInputModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: PvOTPInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.8", ngImport: i0, type: PvOTPInputModule, declarations: [OTPInput], imports: [CommonModule, ReactiveFormsModule], exports: [OTPInput] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: PvOTPInputModule, imports: [CommonModule, ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: PvOTPInputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ReactiveFormsModule],
                    declarations: [OTPInput],
                    exports: [OTPInput],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { OTPInput, PvOTPInputModule };
